import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GridFilterModel } from '@mui/x-data-grid/models/gridFilterModel';
import { GridFilterItem, GridLinkOperator } from '@mui/x-data-grid/models/gridFilterItem';
import { GridEnrichedColDef } from '@mui/x-data-grid';
import {
  GenericModelList,
  GenericRelatedModelList,
  Item,
  SumRowsType,
  useAPIWrapper,
} from 'drf-react-by-schema';

import { Link, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

import { GENERAL_ADMINS, Permission } from 'src/constants/Permission';
import { Layout } from 'src/styles';
import { AzeiteOrOleoProductionStatus } from 'src/constants';
import { useAppSelector } from 'src/app/hooks';
import { useHasPermissionsMulti } from 'src/hooks/useHasPermissions';
import { appDispatch, getAzeiteOrOleoKey, getAzeiteOrOleoName } from 'src/utils/utils';
import { setPage } from 'src/features/pageSlice';

import AzeiteOrOleoProductionCard from 'src/components/AzeiteOrOleoProductionCard';
import AzeiteOrOleoNoResultsCard from 'src/components/AzeiteOrOleoNoResultsCard';
import StatusChip from 'src/components/StatusChip';
import { ProductionZone } from 'src/constants/ProductionZone';
import { ListHolder } from 'src/styles/styledComponents';

const MODEL = 'azeite_or_oleo_production';

export default function AzeiteOrOleoProductionList() {
  const { key, perfil, isMobile } = useAppSelector((state) => state.page);
  const { getGenericModel } = useAPIWrapper();
  const [isAdmin, isGroupAdmin] = useHasPermissionsMulti([
    GENERAL_ADMINS,
    [Permission.COORDENADORA_GRUPO],
  ]);
  const [filters, setFilters] = useState<GridFilterModel | undefined>();
  const [zoneFilter, setZoneFilter] = useState<ProductionZone>(ProductionZone.SELF);
  const [checkedStatus, setCheckedStatus] = useState<AzeiteOrOleoProductionStatus>();

  const [URLSearchParams] = useSearchParams();
  const statusFromURL = URLSearchParams.get('status') as AzeiteOrOleoProductionStatus | null;
  const perfilIdFromURL = URLSearchParams.get('perfil') as AzeiteOrOleoProductionStatus | null;

  const [azeiteOrOleo, label] = useMemo(() => {
    return [getAzeiteOrOleoKey(key), getAzeiteOrOleoName({ pageKey: key, title: true })];
  }, [key]);

  const indexField = 'lote';

  const hiddenFields = useMemo(() => {
    const newHiddenFields = ['azeite_or_oleo'];
    if (checkedStatus === AzeiteOrOleoProductionStatus.READY_TO_PROCESS) {
      newHiddenFields.push('data_armazenamento', 'volume_produzido');
    }
    return newHiddenFields;
  }, [checkedStatus]);

  const columnFields = useMemo(() => {
    const newColumnFields = ['azeite_or_oleo', 'lote', 'data_registro'];
    if (isAdmin) {
      newColumnFields.push(...['perfil', 'grupo', 'comunidadeProduction']);
    } else if (isGroupAdmin && zoneFilter !== 'myProduction') {
      newColumnFields.push(...['perfil']);
    }
    newColumnFields.push(
      ...[
        'status',
        'volume_produzido',
        'data_quebra',
        'peso_amendoas',
        'data_armazenamento',
        'ajudantes',
      ]
    );
    return newColumnFields;
  }, [isAdmin, isGroupAdmin, zoneFilter]);

  const minWidthFields = useMemo(() => {
    const newMinwidthFields = {
      data_registro: 150,
      ...(isAdmin && {
        grupo: 100,
        comunidadeProduction: 150,
      }),
      status: 120,
      data_quebra: 100,
      data_armazenamento: 100,
      ajudantes: 150,
    };
    return newMinwidthFields;
  }, [isAdmin]);

  const customColumnOperations: (column: GridEnrichedColDef) => GridEnrichedColDef = useCallback(
    (column) => {
      if (column.field === 'status') {
        column.renderCell = (params: Item) => {
          if (!azeiteOrOleo) {
            return null;
          }
          return (
            <StatusChip
              type={azeiteOrOleo}
              status={params.row.status.value as AzeiteOrOleoProductionStatus}
            />
          );
        };
      }
      return column;
    },
    [azeiteOrOleo]
  );

  const sumRows: SumRowsType | undefined = useMemo(() => {
    return checkedStatus !== AzeiteOrOleoProductionStatus.READY_TO_PROCESS
      ? {
          rows: [
            {
              field: 'volume_produzido',
              prefix: 'Total produzido: ',
              suffix: ' litros',
            },
          ],
          severity: 'info',
        }
      : undefined;
  }, [checkedStatus]);

  const defaultFilter = useMemo(() => {
    if (!perfil) {
      return;
    }
    const emptyItems: GridFilterItem[] = checkedStatus
      ? [
          {
            columnField: 'status',
            value: checkedStatus,
            operatorValue: 'equals',
          },
        ]
      : [];

    if (isGroupAdmin) {
      switch (zoneFilter) {
        case ProductionZone.GROUP:
          if (perfil.grupo) {
            emptyItems.push({
              columnField: ProductionZone.GROUP,
              value: perfil.grupo.id,
              operatorValue: 'equals',
            });
          }
          break;
        case ProductionZone.COMMUNITY:
          if (perfil.comunidade) {
            emptyItems.push({
              columnField: ProductionZone.COMMUNITY,
              value: perfil.comunidade?.id,
              operatorValue: 'equals',
            });
          }
          break;
      }
    }

    const newDefaultFilter = {
      linkOperator: GridLinkOperator.And,
      items: azeiteOrOleo
        ? [
            ...emptyItems,
            {
              columnField: 'azeite_or_oleo',
              value: azeiteOrOleo,
              operatorValue: 'equals',
            },
          ]
        : [...emptyItems],
    };
    return newDefaultFilter;
  }, [azeiteOrOleo, isGroupAdmin, perfil, zoneFilter, checkedStatus]);

  const handleClickFilter = useCallback(
    (filterClicked: AzeiteOrOleoProductionStatus) => {
      if (!azeiteOrOleo) {
        return;
      }
      setCheckedStatus(filterClicked === checkedStatus ? undefined : filterClicked);
    },
    [azeiteOrOleo, checkedStatus]
  );

  const handleMine = () => {
    setZoneFilter(ProductionZone.SELF);
  };
  const handleGrupo = () => {
    setZoneFilter(ProductionZone.GROUP);
  };
  const handleComunidade = () => {
    setZoneFilter(ProductionZone.COMMUNITY);
  };

  useEffect(() => {
    setFilters(defaultFilter);
  }, [defaultFilter]);

  useEffect(() => {
    if (statusFromURL) {
      setCheckedStatus(statusFromURL);
    }
  }, [statusFromURL]);

  useEffect(() => {
    if (!perfilIdFromURL || !isAdmin) {
      appDispatch(
        setPage({
          subTitle: null,
        })
      );
      return;
    }

    const fetchPerfil = async () => {
      const newPerfilFromURL = await getGenericModel({
        model: 'perfil',
        id: perfilIdFromURL,
      });

      if (newPerfilFromURL !== false) {
        appDispatch(
          setPage({
            subTitle: newPerfilFromURL.data.nome,
          })
        );
      }
    };

    fetchPerfil();
  }, [perfilIdFromURL, isAdmin]);

  if (!perfil || !azeiteOrOleo || !filters) {
    return null;
  }

  return (
    <ListHolder isMobile={isMobile}>
      {isGroupAdmin && (perfil.grupo || perfil.comunidade) && (
        <Box sx={{ mb: 2 }}>
          <Button
            onClick={zoneFilter !== ProductionZone.SELF ? handleMine : undefined}
            size="small"
            variant={zoneFilter === ProductionZone.SELF ? 'contained' : 'outlined'}>
            Minha produção
          </Button>
          {perfil.grupo && (
            <Button
              onClick={zoneFilter !== ProductionZone.GROUP ? handleGrupo : undefined}
              size="small"
              variant={zoneFilter === ProductionZone.GROUP ? 'contained' : 'outlined'}
              sx={{ ml: 2 }}>
              Grupo
            </Button>
          )}
          {perfil.comunidade && (
            <Button
              onClick={zoneFilter !== ProductionZone.COMMUNITY ? handleComunidade : undefined}
              size="small"
              variant={zoneFilter === ProductionZone.COMMUNITY ? 'contained' : 'outlined'}
              sx={{ ml: 2 }}>
              Comunidade
            </Button>
          )}
        </Box>
      )}
      <Box sx={Layout.topBarOverList}>
        {Object.values(AzeiteOrOleoProductionStatus).map((status) => (
          <StatusChip
            key={`filter_${status}`}
            type={azeiteOrOleo}
            variant={checkedStatus === status ? 'filled' : 'outlined'}
            onClick={() => {
              handleClickFilter(status);
            }}
            status={status}
            size="medium"
          />
        ))}
      </Box>
      {(isAdmin && !perfilIdFromURL) || zoneFilter !== 'myProduction' ? (
        <GenericModelList
          model={MODEL}
          indexField={indexField}
          columnFields={columnFields}
          hiddenFields={hiddenFields}
          minWidthFields={minWidthFields}
          customColumnOperations={customColumnOperations}
          sumRows={sumRows}
          creatableFields={[]}
          paginationMode="server"
          indexFieldBasePath={`/${key}/view/`}
          LinkComponent={Link}
          MobileListRenderItem={AzeiteOrOleoProductionCard}
          MobileRenderNoResults={AzeiteOrOleoNoResultsCard}
          tableAutoHeight={false}
          defaultFilter={filters}
          hideQuickFilterBar={!isAdmin && !isGroupAdmin}
          hasHeader={false}
        />
      ) : (
        <GenericRelatedModelList
          model="perfil"
          id={isAdmin && perfilIdFromURL ? perfilIdFromURL : perfil.id}
          relatedModel={MODEL}
          label={label}
          indexField={indexField}
          columnFields={columnFields}
          hiddenFields={hiddenFields}
          minWidthFields={minWidthFields}
          customColumnOperations={customColumnOperations}
          sumRows={sumRows}
          creatableFields={[]}
          paginationMode="server"
          indexFieldBasePath={`/${key}/view/`}
          LinkComponent={Link}
          MobileListRenderItem={AzeiteOrOleoProductionCard}
          MobileRenderNoResults={AzeiteOrOleoNoResultsCard}
          tableAutoHeight={true}
          noCardWrapper={true}
          defaultFilter={filters}
          hideQuickFilterBar={!isAdmin}
        />
      )}
    </ListHolder>
  );
}
